import { z } from 'zod';
import { uuid } from './posting.js';
/**
 * **Die Verträge der Belegvorlagen, Stufe 0** — Auftrag `BV-nullschema`
 * (2026-10-15), Task 1 des Plans
 * `docs/superpowers/plans/2026-10-15-belegvorlagen-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-10-15-belegvorlagen-design.md` Abschnitte 4,
 * 5.1, 5.2, 6.1, 9 und 10.
 *
 * **Dieser Vertrag ist der erste Commit der Ära** (Naht 1 des Plans): Die
 * parallelen Aufträge `BV-nullrender`, `BV-nulldienst`, `BV-nullvorschau` und
 * `BV-nullseite` zweigen von ihm ab. Was hier steht, ist deshalb die eine
 * Liste — der Dienst liefert den Katalog aus `BELEGVORLAGE_KATALOG` und nicht
 * aus einer zweiten, die Oberfläche liest ihn vom Server (Spec 10.1).
 *
 * **Kein freier CSS-Wert, nirgends** (`D-8`). In Stufe 0 gibt es überhaupt
 * keinen Wert, der ins CSS geht: Texte gehen als maskierte Werte in die
 * Vorlage, Schalter sind Aufzählungen. Stufe 1 fügt Skalen hinzu
 * (`seitenrand`, `grundschrift`, …) — ebenfalls Aufzählungen, die
 * `gestaltungsCss()` über eine Tabelle im Code auf Regeln abbildet. Ein Feld
 * vom Typ `string`, das in eine CSS-Regel fliesst, wäre der Fehler, auch wenn
 * dieser Vertrag es „prüfte".
 *
 * **Kein Aus-Schalter für einen Pflichtblock** (`D-9`, Spec 6.2, Riegel 1).
 * Die einzigen Schalter der Stufe 0 sind `bankverbindung` (Hausstil) und
 * `zahlungsQr` (Rechnung); beide tragen keine Pflichtangabe. Die
 * Pflichtblöcke stehen in `BELEGVORLAGE_PFLICHTBLOECKE` — als Auskunft für den
 * Katalog, nicht als schaltbare Liste.
 *
 * **Ein fehlendes Feld heisst Werk** (Spec 5.1). Jedes Feld ist optional;
 * `{ schemaFassung: '1' }` ist die leere Gestaltung und rendert byte-gleich
 * zur Werksvorlage (`E-1924`, Auftrag `BV-nullrender`). Felder, die Stufe 1
 * hinzufügt, kommen optional unter derselben `schemaFassung` hinzu; eine
 * Fassung der Stufe 0 bleibt dann gültig.
 *
 * **`.strict()` auf jeder Ebene** (`E-1923`): Ein unbekannter Schlüssel ist ein
 * Fehler und kein stilles Weglassen. Eine Fassung ist unveränderlich
 * (Migration `0317`, `E-1922`); was beim Anfügen unbemerkt durchrutscht, steht
 * dort für immer.
 *
 * **Das Leseschema** ist `belegvorlageEinstellungenSchema` — der Dienst liest
 * die Spalte als `belegvorlageEinstellungenSchema.safeParse(zeile.einstellungen)`,
 * damit `pnpm sql:jsonb` die Spalte `belegvorlage_fassung.einstellungen`
 * diesem Schema zuordnet und jede Fixtur dagegen hält (Spec 5.1, `D-8`).
 *
 * **Was hier NICHT steht: die Regel, die Art und Layout des Kopfes braucht.**
 * Beim Anfügen einer Fassung steht die Art nicht im Rumpf, sondern in der
 * vorhandenen Zeile `belegvorlage` — dieselbe Lage wie bei den
 * E-Mail-Vorlagen. Die Regel steht deshalb genau einmal, als
 * `belegvorlageEinstellungenFuer()` hier, und der Dienst ruft sie mit dem
 * gelesenen Kopf auf; die Anlage und die Vorschau rufen sie mit dem Rumpf auf.
 */
// --- Grundwerte ------------------------------------------------------------
/**
 * Die Fassung der Einstellungsform. Stufe 0 und 1 schreiben `'1'`, Stufe 2
 * `'2'` (Migration `0319`, Spec 4.6). Steht im Objekt UND in der Bedingung
 * `belegvorlage_fassung_schema_fassung` (Migration `0317`).
 */
export const BELEGVORLAGE_SCHEMA_FASSUNG = '1';
/**
 * Die fünf Layouts — die Schlüssel aus `TEMPLATE_BY_TYPE`
 * (`apps/api/src/render/template-registry.ts`, `templateKeys()`), nicht die
 * elf Belegarten (`D-2`).
 *
 * **Drei Orte, eine Liste, ein Wächter:** diese Aufzählung, die Bedingung
 * `belegvorlage_layout_bekannt` in Migration `0317` und `templateKeys()`.
 * `E-1920` (`packages/schema/test/enforcement/belegvorlagen.test.ts`) hält alle
 * drei gegeneinander; ein sechstes Layout macht ihn rot, und das ist sein
 * Zweck. `packages/contracts` kann `apps/api` nicht importieren — deshalb
 * steht die Liste hier ein zweites Mal und wird geprüft statt abgeleitet.
 */
export const BELEGVORLAGE_LAYOUTS = [
    'invoice',
    'credit-note',
    'cancellation',
    'quote',
    'delivery-note',
];
export const belegvorlageLayoutSchema = z.enum(BELEGVORLAGE_LAYOUTS);
/** Die zwei Arten einer Vorlage (`belegvorlage_art_bekannt`, `D-3`). */
export const belegvorlageArtSchema = z.enum(['hausstil', 'beleg']);
/**
 * Die vier Zustände einer Fassung (`belegvorlage_fassung_status_bekannt`,
 * `D-5`) — dieselben Wörter wie bei den E-Mail-Vorlagen.
 *
 * Als LESESCHEMA vollständig, als ZIEL nirgends: Die Übergänge haben eigene
 * Endpunkte (`…/scharfschalten`, `…/verwerfen`), `abgeloest` setzt allein der
 * Dienst beim Scharfschalten der Nachfolgerin, und ein Statusfeld im Rumpf gibt
 * es nicht. Erlaubt sind `entwurf→scharf`, `entwurf→verworfen`,
 * `scharf→abgeloest` (`E-1922`).
 */
export const BELEGVORLAGE_STATUS = ['entwurf', 'scharf', 'abgeloest', 'verworfen'];
export const belegvorlageStatusSchema = z.enum(BELEGVORLAGE_STATUS);
/**
 * Der Sprachcode einer Belegvorlage.
 *
 * **Zwei Kleinbuchstaben und ausdrücklich KEINE Aufzählung** — dieselbe
 * Begründung wie bei den E-Mail-Vorlagen (`D-6` dort): `belegvorlage.sprache`
 * zeigt auf `document_language(code)`, und diese Tabelle ist Daten. Ein
 * unbekannter Code läuft in den Fremdschlüssel.
 */
export const belegvorlageSpracheSchema = z
    .string()
    .regex(/^[a-z]{2}$/, 'Ein Sprachcode besteht aus zwei Kleinbuchstaben');
/** Die Höchstlänge eines Freitexts der Stufe 0 (Spec 5.2, `Text500`). */
export const BELEGVORLAGE_TEXT_HOECHSTLAENGE = 500;
/** Die Höchstlänge der freien Bemerkung an einer Fassung. */
export const BELEGVORLAGE_BEMERKUNG_HOECHSTLAENGE = 200;
/**
 * Ein Steuerzeichen, das in einem Freitext nichts verloren hat: C0 ausser dem
 * Zeilenumbruch (`\n`), `DEL` und C1. Auch `\r` — ein Browser liefert den Wert
 * eines Textfelds mit `\n`, und ein `\r` im PDF ist ein Zeichen ohne Glyphe.
 */
const STEUERZEICHEN = /[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/u;
/**
 * Ein Klartext von 1 bis `hoechst` Zeichen ohne Steuerzeichen ausser dem
 * Zeilenumbruch (Spec 5.2, `Text500`).
 *
 * **Nicht gestrafft, nicht getrimmt** — der Text geht so in die Vorlage, wie er
 * geschrieben wurde; ein Zeilenumbruch ist Absicht. Abgewiesen wird benannt,
 * nie still bereinigt: eine Fassung ist unveränderlich, und was der Vertrag
 * still umschreibt, steht danach anders in der Datenbank, als der Anwender es
 * sah. Ein Text nur aus Weissraum ist leer und wird abgewiesen — „Werk" heisst
 * hier: Feld weglassen.
 *
 * **Kein Markup** (Stufe 0): Ein `<b>` ist Text und erscheint als Text, die
 * Vorlage maskiert ihn (`E-1927`). Auszeichnung kommt erst in Stufe 1, als
 * Struktur aus dem Positionstext-Zerleger.
 */
function klartext(hoechst) {
    return z
        .string()
        .min(1, 'Der Text darf nicht leer sein')
        .max(hoechst, `Der Text darf höchstens ${String(hoechst)} Zeichen lang sein`)
        .superRefine((wert, ctx) => {
        if (wert.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Der Text darf nicht nur aus Leerraum bestehen',
            });
        }
        if (STEUERZEICHEN.test(wert)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Der Text enthält ein Steuerzeichen; erlaubt ist allein der Zeilenumbruch',
            });
        }
    });
}
/** Ein Freitext der Stufe 0 (Spec 5.2, `Text500`). */
export const belegvorlageTextSchema = klartext(BELEGVORLAGE_TEXT_HOECHSTLAENGE);
/** Der Schalter „zeigen/ausblenden" — Werk ist immer `zeigen`. */
export const belegvorlageSichtbarkeitSchema = z.enum(['zeigen', 'ausblenden']);
// --- Einstellungen ---------------------------------------------------------
/** Die drei Fusstexte einer Sprache (Spec 5.2, `Fusstexte`). */
export const belegvorlageFusstexteSchema = z
    .object({
    links: belegvorlageTextSchema.optional(),
    mitte: belegvorlageTextSchema.optional(),
    rechts: belegvorlageTextSchema.optional(),
})
    .strict();
/**
 * Die Einstellungen des Hausstils, Stufe 0 (Spec 5.2).
 *
 * `fusszeile` steht **je Sprache**: Der Hausstil ist einer für alle Belege,
 * der Fusstext ist Text und damit Sprache. Die Schlüssel `de` und `en` sind
 * hier ausnahmsweise fest — ein Objekt mit beliebigen Sprachschlüsseln wäre ein
 * freies Wörterbuch, und `.strict()` könnte einen Tippfehler (`'dee'`) nicht
 * mehr abweisen. Eine dritte Sprache in `document_language` ist dann eine
 * Vertragsänderung; die Vorlagendateien (`<layout>.<sprache>.html`) sind es
 * ohnehin.
 */
export const hausstilEinstellungenSchema = z
    .object({
    schemaFassung: z.literal(BELEGVORLAGE_SCHEMA_FASSUNG),
    fusszeile: z
        .object({
        de: belegvorlageFusstexteSchema.optional(),
        en: belegvorlageFusstexteSchema.optional(),
    })
        .strict()
        .optional(),
    /**
     * Bankverbindung im Fuss. `ausblenden` nimmt sie aus dem Fuss, macht den
     * Zahlungsabsatz zur Kurzform und nimmt den Zahlungs-QR weg — der
     * Lastschriftabsatz bleibt (Spec 6.2, Riegel 2, `E-1927`).
     */
    bankverbindung: belegvorlageSichtbarkeitSchema.optional(),
})
    .strict();
/** Die Einstellungen einer Belegvorlage (Layout und Sprache), Stufe 0 (Spec 5.2). */
export const belegEinstellungenSchema = z
    .object({
    schemaFassung: z.literal(BELEGVORLAGE_SCHEMA_FASSUNG),
    /** Ersetzt den Einleitungssatz vor der Positionstabelle. */
    einleitung: belegvorlageTextSchema.optional(),
    /**
     * Steht **zusätzlich** nach den Werkssätzen — Widerrufshinweis, AGB und der
     * beschiedene Zahlungsabsatz bleiben (`D-12`).
     */
    schluss: belegvorlageTextSchema.optional(),
    /** Nur beim Layout `invoice` (`belegvorlageEinstellungenFuer`). */
    zahlungsQr: belegvorlageSichtbarkeitSchema.optional(),
})
    .strict();
/**
 * **Das Leseschema der Spalte `belegvorlage_fassung.einstellungen`** (`D-8`,
 * `E-1923`).
 *
 * Die Spalte trägt je nach Art des Kopfes die eine oder die andere Form, und
 * die Zeile der Fassung kennt ihre Art nicht — deshalb die Vereinigung. Die
 * leere Gestaltung `{ schemaFassung: '1' }` passt auf beide, und das ist richtig:
 * Sie heisst in beiden Fällen „Werk". Wer die Art kennt, prüft enger mit
 * `belegvorlageEinstellungenFuer()`.
 */
export const belegvorlageEinstellungenSchema = z.union([
    hausstilEinstellungenSchema,
    belegEinstellungenSchema,
]);
/**
 * Das Einstellungsschema für einen bestimmten Kopf — **die eine Stelle**, an
 * der die art- und layoutabhängige Regel steht.
 *
 * - Hausstil: `hausstilEinstellungenSchema`.
 * - Belegvorlage: `belegEinstellungenSchema`, und `zahlungsQr` nur beim Layout
 *   `invoice` (Spec 5.2) — ein Angebot hat keinen Zahlungs-QR, den man
 *   ausblenden könnte, und ein Schalter ohne Wirkung wäre eine Behauptung.
 */
export function belegvorlageEinstellungenFuer(kopf) {
    if (kopf.art === 'hausstil')
        return hausstilEinstellungenSchema;
    const layout = kopf.layout;
    return belegEinstellungenSchema.superRefine((wert, ctx) => {
        if (wert.zahlungsQr !== undefined && layout !== 'invoice') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['zahlungsQr'],
                message: `Den Zahlungs-QR gibt es nur auf der Rechnung (Layout invoice), nicht beim Layout ${String(layout)}`,
            });
        }
    });
}
// --- Eingaben --------------------------------------------------------------
/** Die freie Bemerkung an einer Fassung — `undefined` und `null` werden `null`. */
const bemerkungSchema = klartext(BELEGVORLAGE_BEMERKUNG_HOECHSTLAENGE)
    .nullish()
    .transform((wert) => wert ?? null);
/**
 * Eine Fassung, wie ein Aufrufer sie anfügt (`POST …/:id/fassungen`).
 *
 * **Kein Statusfeld**: Eine neue Fassung ist immer `entwurf` (Spec 10.1). Die
 * Einstellungen werden hier gegen das Leseschema geprüft; die engere Regel nach
 * Art und Layout des Kopfes wendet der Dienst mit `belegvorlageEinstellungenFuer()`
 * an, weil der Kopf nicht im Rumpf steht.
 */
export const belegvorlageFassungEingabeSchema = z
    .object({
    einstellungen: belegvorlageEinstellungenSchema,
    bemerkung: bemerkungSchema,
})
    .strict();
/**
 * Eine Vorlage anlegen — Kopf und erste Fassung (`entwurf`) in einem Aufruf
 * (`POST /api/v1/belegvorlagen`).
 *
 * Der Spiegel von `belegvorlage_art_felder` (`E-1920`), beidseitig wie dort;
 * die Bedingung in der Datenbank bleibt die massgebliche. Dazu die
 * Einstellungsregel des Kopfes, der hier im Rumpf steht.
 */
export const belegvorlageAnlageSchema = z
    .object({
    art: belegvorlageArtSchema,
    layout: belegvorlageLayoutSchema
        .nullish()
        .transform((wert) => wert ?? null),
    sprache: belegvorlageSpracheSchema.nullish().transform((wert) => wert ?? null),
    fassung: belegvorlageFassungEingabeSchema,
})
    .strict()
    .superRefine((wert, ctx) => {
    if (wert.art === 'hausstil' && (wert.layout !== null || wert.sprache !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Der Hausstil trägt weder Layout noch Sprache: Er gilt für alle Belege; die Fusstexte stehen je Sprache in seinen Einstellungen.',
        });
        return;
    }
    if (wert.art === 'beleg' && (wert.layout === null || wert.sprache === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Belegvorlage trägt ein Layout UND eine Sprache.',
        });
        return;
    }
    const geprueft = belegvorlageEinstellungenFuer({ art: wert.art, layout: wert.layout }).safeParse(wert.fassung.einstellungen);
    if (!geprueft.success) {
        for (const issue of geprueft.error.issues) {
            ctx.addIssue({ ...issue, path: ['fassung', 'einstellungen', ...issue.path] });
        }
    }
});
/**
 * Der Rumpf der Vorschau (`POST /api/v1/belegvorlagen/vorschau`, Spec 9) —
 * **ungespeicherte** Einstellungen beider Ebenen, Layout und Sprache.
 *
 * Kein Feld nennt einen Beleg: Gerendert wird ausschliesslich der feste,
 * erfundene Musterbeleg (`D-22`, `E-1929`). Ein Feld `documentId` gäbe es nur,
 * um es zu missbrauchen.
 */
export const belegvorlageVorschauEingabeSchema = z
    .object({
    layout: belegvorlageLayoutSchema,
    sprache: belegvorlageSpracheSchema,
    hausstil: hausstilEinstellungenSchema,
    beleg: belegEinstellungenSchema,
})
    .strict()
    .superRefine((wert, ctx) => {
    const geprueft = belegvorlageEinstellungenFuer({ art: 'beleg', layout: wert.layout }).safeParse(wert.beleg);
    if (!geprueft.success) {
        for (const issue of geprueft.error.issues) {
            ctx.addIssue({ ...issue, path: ['beleg', ...issue.path] });
        }
    }
});
// --- Ansichten -------------------------------------------------------------
/** Eine Fassung, wie die Schnittstelle sie zeigt. */
export const belegvorlageFassungAnsichtSchema = z.object({
    id: uuid,
    vorlageId: uuid,
    fassungNr: z.number().int(),
    einstellungen: belegvorlageEinstellungenSchema,
    status: belegvorlageStatusSchema,
    bemerkung: z.string().nullable(),
    scharfgeschaltetVon: z.string().nullable(),
    scharfgeschaltetAm: z.string().nullable(),
    createdAt: z.string(),
    createdBy: z.string(),
});
/** Kopf und vollständige Fassungshistorie, jüngste Fassung zuerst (`GET …/:id`). */
export const belegvorlageAnsichtSchema = z.object({
    id: uuid,
    art: belegvorlageArtSchema,
    layout: belegvorlageLayoutSchema.nullable(),
    sprache: z.string().nullable(),
    createdAt: z.string(),
    createdBy: z.string(),
    fassungen: z.array(belegvorlageFassungAnsichtSchema),
});
/**
 * Ein Eintrag der Liste: eine **Stelle** — der Hausstil oder ein Paar aus
 * Layout und Sprache —, mit ihrer Vorlage, falls es eine gibt.
 *
 * **Die Liste führt jede Stelle, auch die ohne Zeile** (Spec 10.1: „je mit
 * scharfer Fassung oder Werk"). Die Werksvorlage ist keine Zeile (`D-4`);
 * `vorlageId: null` heisst „noch nie gestaltet", `scharfeFassung: null` heisst
 * „rendert mit der Werksvorlage" — beides ohne ein drittes Feld, das dasselbe
 * noch einmal sagte.
 */
export const belegvorlageListeEintragSchema = z.object({
    art: belegvorlageArtSchema,
    layout: belegvorlageLayoutSchema.nullable(),
    sprache: z.string().nullable(),
    vorlageId: uuid.nullable(),
    scharfeFassung: belegvorlageFassungAnsichtSchema.nullable(),
    fassungenAnzahl: z.number().int(),
});
/** Die Liste: der Hausstil zuerst, danach je Layout und Sprache (`GET /api/v1/belegvorlagen`). */
export const belegvorlageListeSchema = z.object({
    vorlagen: z.array(belegvorlageListeEintragSchema),
});
// --- Katalog ---------------------------------------------------------------
/**
 * Die Pflichtblöcke (Spec 6.1) — ab Stufe 0 alle acht, keiner ausblendbar.
 *
 * Diese Liste ist eine **Auskunft** für den Katalog und die Oberfläche (das
 * Schloss in Stufe 1), kein Schalterregister: Der Vertrag kennt für keinen von
 * ihnen einen Schalter (`D-9`, Riegel 1).
 */
export const BELEGVORLAGE_PFLICHTBLOECKE = [
    {
        schluessel: 'absender-und-rechtsform',
        bezeichnung: 'Absenderzeile und Fuss der Rechtsform',
        pflichtangabe: 'Name und Anschrift des Leistenden, Steuernummer oder USt-IdNr., Angaben nach Rechtsform',
    },
    {
        schluessel: 'anschriftfeld',
        bezeichnung: 'Anschriftfeld',
        pflichtangabe: 'Name und Anschrift des Empfängers',
    },
    {
        schluessel: 'infoblock',
        bezeichnung: 'Infoblock',
        pflichtangabe: 'Ausstellungsdatum, fortlaufende Nummer, Zeitpunkt der Leistung',
    },
    {
        schluessel: 'positionstabelle',
        bezeichnung: 'Positionstabelle mit Menge, Einheit, Bezeichnung und Betrag',
        pflichtangabe: 'Menge und Art der Leistung; Entgelt',
    },
    {
        schluessel: 'summen',
        bezeichnung: 'Summen mit Aufschlüsselung nach Steuersätzen',
        pflichtangabe: 'Entgelt je Satz, Steuersatz, Steuerbetrag',
    },
    {
        schluessel: 'hinweise',
        bezeichnung: 'Hinweise',
        pflichtangabe: 'Hinweis auf Steuerbefreiung, Kleinunternehmer, Steuerschuldnerschaft',
    },
    {
        schluessel: 'lastschriftabsatz',
        bezeichnung: 'Lastschriftabsatz',
        pflichtangabe: 'SEPA-Vorabankündigung, wo sie am Beleg eingefroren ist',
    },
    {
        schluessel: 'ueberschrift',
        bezeichnung: 'Überschrift',
        pflichtangabe: 'Belegbezeichnung, etwa „Rechnungskorrektur" oder „Stornorechnung" — nie „Gutschrift"',
    },
];
/** Ein Pflichtblock im Katalog. */
export const belegvorlagePflichtblockSchema = z.object({
    schluessel: z.string(),
    bezeichnung: z.string(),
    pflichtangabe: z.string(),
});
/**
 * Ein Feld der Einstellungen im Katalog — die Oberfläche baut ihr Formular
 * daraus und schreibt keine zweite Liste (Spec 10.1, `E-1943` ab Stufe 1).
 *
 * - `pfad`: der Pfad im Einstellungsobjekt, Glieder mit Punkt; `{sprache}`
 *   steht für den Sprachcode (`fusszeile.{sprache}.links`).
 * - `art`: `text` (Klartext bis `hoechstlaenge`) oder `schalter` (`werte`,
 *   Werk ist `vorgabe`).
 * - `layouts`: `null` = jedes Layout; sonst nur diese (der Zahlungs-QR).
 */
export const belegvorlageKatalogFeldSchema = z.object({
    ebene: belegvorlageArtSchema,
    pfad: z.string(),
    art: z.enum(['text', 'schalter']),
    bezeichnung: z.string(),
    hoechstlaenge: z.number().int().nullable(),
    werte: z.array(z.string()).nullable(),
    vorgabe: z.string().nullable(),
    layouts: z.array(belegvorlageLayoutSchema).nullable(),
});
/**
 * Der Katalog (`GET /api/v1/belegvorlagen/katalog`).
 *
 * `sprachen` liest der Dienst aus `document_language` (Daten, keine
 * Aufzählung); alles andere kommt aus `BELEGVORLAGE_KATALOG`.
 */
export const belegvorlageKatalogSchema = z.object({
    schemaFassung: z.literal(BELEGVORLAGE_SCHEMA_FASSUNG),
    layouts: z.array(belegvorlageLayoutSchema),
    sprachen: z.array(z.string()),
    felder: z.array(belegvorlageKatalogFeldSchema),
    pflichtbloecke: z.array(belegvorlagePflichtblockSchema),
});
const SICHTBARKEIT_WERTE = belegvorlageSichtbarkeitSchema.options;
/**
 * Der Katalog der Stufe 0 ohne die Sprachen — **aus diesem Vertrag**, nicht
 * abgeschrieben. Der Unit-Test hält jedes Feld gegen das Schema: Ein Pfad, den
 * die Einstellungen nicht kennen, oder ein Feld der Einstellungen ohne
 * Katalogzeile macht ihn rot.
 */
export const BELEGVORLAGE_KATALOG = {
    schemaFassung: BELEGVORLAGE_SCHEMA_FASSUNG,
    layouts: [...BELEGVORLAGE_LAYOUTS],
    felder: [
        ...['links', 'mitte', 'rechts'].map((spalte) => ({
            ebene: 'hausstil',
            pfad: `fusszeile.{sprache}.${spalte}`,
            art: 'text',
            bezeichnung: `Fusszeile ${spalte}`,
            hoechstlaenge: BELEGVORLAGE_TEXT_HOECHSTLAENGE,
            werte: null,
            vorgabe: null,
            layouts: null,
        })),
        {
            ebene: 'hausstil',
            pfad: 'bankverbindung',
            art: 'schalter',
            bezeichnung: 'Bankverbindung',
            hoechstlaenge: null,
            werte: [...SICHTBARKEIT_WERTE],
            vorgabe: 'zeigen',
            layouts: null,
        },
        {
            ebene: 'beleg',
            pfad: 'einleitung',
            art: 'text',
            bezeichnung: 'Einleitung (ersetzt den Einleitungssatz)',
            hoechstlaenge: BELEGVORLAGE_TEXT_HOECHSTLAENGE,
            werte: null,
            vorgabe: null,
            layouts: null,
        },
        {
            ebene: 'beleg',
            pfad: 'schluss',
            art: 'text',
            bezeichnung: 'Schluss (zusätzlich nach den Werkssätzen)',
            hoechstlaenge: BELEGVORLAGE_TEXT_HOECHSTLAENGE,
            werte: null,
            vorgabe: null,
            layouts: null,
        },
        {
            ebene: 'beleg',
            pfad: 'zahlungsQr',
            art: 'schalter',
            bezeichnung: 'Zahlungs-QR',
            hoechstlaenge: null,
            werte: [...SICHTBARKEIT_WERTE],
            vorgabe: 'zeigen',
            layouts: ['invoice'],
        },
    ],
    pflichtbloecke: BELEGVORLAGE_PFLICHTBLOECKE.map((b) => ({ ...b })),
};
//# sourceMappingURL=belegvorlagen.js.map